'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { callbackRequestSchema, CallbackRequestData } from '@/lib/schema';
import { submitCallbackRequest } from '@/lib/api';
import { Input } from '@/app/components/ui/Input';
import { Button } from '@/app/components/ui/Button';
import { Label } from '@/app/components/ui/Label';
import { Textarea } from '@/app/components/ui/Textarea';
import { Alert } from '@/app/components/ui/Alert';
import { CheckCircle2 } from 'lucide-react';

interface CallbackFormProps {
  source?: string;
  onSuccess?: () => void;
  initialValues?: Partial<CallbackRequestData>;
}

export function CallbackForm({ source = 'website', onSuccess, initialValues }: CallbackFormProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<CallbackRequestData>({
    resolver: yupResolver(callbackRequestSchema) as any,
    defaultValues: {
      source,
      ...initialValues,
    },
  });

  const onSubmit = async (data: CallbackRequestData) => {
    setSubmitError(null);
    try {
      await submitCallbackRequest({ ...data, source });
      setIsSuccess(true);
      reset();
      if (onSuccess) onSuccess();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Something went wrong');
    }
  };

  if (isSuccess) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        className="text-center"
      >
        <motion.div 
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200, damping: 10 }}
          className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <CheckCircle2 className="w-8 h-8" />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3 className="text-3xl font-display text-teal mb-2">Request Received</h3>
          <p className="text-muted-foreground mb-4">
            Thank you! We have received your callback request and will be in touch shortly.
          </p>
          <Link href="/">
            <Button 
              variant="outline" 
              className="bg-white hover:bg-muted text-foreground"
            >
              Back to Home
            </Button>
          </Link>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <div className="max-w-xl mx-auto">
      <div className="mb-8">
        <h2 className="text-3xl font-display text-teal mb-2">Request a Callback</h2>
        <p className="text-muted-foreground">Leave your details and we'll call you back.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 text-left">
        {submitError && (
          <Alert variant="error">
            {submitError}
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            placeholder="e.g. Sarah"
            {...register('name')}
            aria-invalid={!!errors.name}
          />
          {errors.name && (
            <p className="text-sm text-destructive font-medium">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            placeholder="07700 900000"
            {...register('phone')}
            aria-invalid={!!errors.phone}
          />
          {errors.phone && (
            <p className="text-sm text-destructive font-medium">{errors.phone.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            placeholder="sarah@example.com"
            {...register('email')}
            aria-invalid={!!errors.email}
          />
          {errors.email && (
            <p className="text-sm text-destructive font-medium">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="reason">How can we help?</Label>
          <Textarea
            id="reason"
            placeholder="I'd like to know more about..."
            {...register('reason')}
            className="min-h-[140px]"
            aria-invalid={!!errors.reason}
          />
          {errors.reason && (
            <p className="text-sm text-destructive font-medium">{errors.reason.message}</p>
          )}
        </div>

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Sending Request...' : 'Request Callback'}
        </Button>

        <p className="text-xs text-muted-foreground text-center mt-4">
          By submitting this form, you agree to our privacy policy. Your data is secure.
        </p>
      </form>
    </div>
  );
}
